import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  frontDeskApi,
  type Notice,
  type NoticeCategory,
  type NoticeInput,
  type NoticeTargetType,
} from '@/api/frontdesk';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const CATEGORY_COLOR: Record<NoticeCategory, string> = {
  ACADEMIC: 'blue',
  FEE: 'gold',
  EVENT: 'magenta',
  GENERAL: 'default',
};

const TARGET_LABEL: Record<NoticeTargetType, string> = {
  ALL: 'Everyone',
  STAFF_ONLY: 'Staff only',
  STUDENTS_ALL: 'All students',
  CLASS_SPECIFIC: 'Specific class',
};

interface FormShape {
  title: string;
  body: string;
  category: NoticeCategory;
  targetType: NoticeTargetType;
  targetClassId?: number;
  effectiveDate: Dayjs;
}

export function NoticesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FRONT_DESK:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FRONT_DESK:EDIT'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(60, 'day'), dayjs().add(30, 'day')]);
  const [categoryFilter, setCategoryFilter] = useState<NoticeCategory | undefined>(undefined);
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<Notice | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<Notice | null>(null);
  const [form] = Form.useForm<FormShape>();

  const params = {
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    category: categoryFilter,
    activeOnly,
  };

  const { data, isFetching } = useQuery({
    queryKey: ['frontdesk', 'notices', params],
    queryFn: () => frontDeskApi.listNotices(params),
  });

  const { data: classes } = useQuery({
    queryKey: ['setup', 'classes'],
    queryFn: setupApi.listClasses,
    staleTime: 5 * 60_000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: NoticeInput = {
        title: values.title.trim(),
        body: values.body.trim(),
        category: values.category,
        targetType: values.targetType,
        targetClassId: values.targetType === 'CLASS_SPECIFIC' ? values.targetClassId ?? null : null,
        effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
      };
      return editing ? frontDeskApi.updateNotice(editing.id, body) : frontDeskApi.createNotice(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Notice updated' : 'Notice published');
      qc.invalidateQueries({ queryKey: ['frontdesk', 'notices'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      frontDeskApi.toggleNotice(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['frontdesk', 'notices'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ category: 'GENERAL', targetType: 'ALL', effectiveDate: dayjs() });
  };
  const openEdit = (row: Notice) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      title: row.title,
      body: row.body,
      category: row.category,
      targetType: row.targetType,
      targetClassId: row.targetClassId ?? undefined,
      effectiveDate: dayjs(row.effectiveDate),
    });
  };
  const open = creating || editing != null;
  const watchedTarget = Form.useWatch('targetType', form);

  const columns: TableProps<Notice>['columns'] = [
    {
      title: 'Effective',
      dataIndex: 'effectiveDate',
      width: 120,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Title',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.title}</Typography.Text>
          <Space size={6}>
            <Tag color={CATEGORY_COLOR[row.category]}>{row.category}</Tag>
            <Tag>{TARGET_LABEL[row.targetType]}{row.targetClassName ? ` · ${row.targetClassName}` : ''}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 160,
      align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setViewing(row)}>
            View
          </Button>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Front Desk"
        title="Notices & announcements"
        description="Publish by category and audience — all, staff, students, or a specific class."
        count={data ? { value: data.length, label: 'in range' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New notice
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        <Select
          allowClear
          placeholder="Category"
          style={{ minWidth: 160 }}
          value={categoryFilter}
          onChange={(v) => setCategoryFilter(v)}
          options={(Object.keys(CATEGORY_COLOR) as NoticeCategory[]).map((k) => ({ value: k, label: k }))}
        />
        <Space size={6}>
          <Switch checked={activeOnly} onChange={setActiveOnly} />
          <Typography.Text type="secondary">Active only</Typography.Text>
        </Space>
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No notices in this view" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data ?? []}
          pagination={{ pageSize: 25 }}
        />
      )}

      <Drawer
        title={editing ? `Edit · ${editing.title}` : 'New notice'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={560}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Publish'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Title" name="title" rules={[{ required: true, max: 300 }]}>
              <Input autoFocus placeholder="One-line headline" />
            </Form.Item>
            <Form.Item label="Body" name="body" rules={[{ required: true, max: 10000 }]}>
              <Input.TextArea autoSize={{ minRows: 5, maxRows: 14 }} />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true }]}
                style={{ width: 200 }}
              >
                <Select
                  options={(Object.keys(CATEGORY_COLOR) as NoticeCategory[]).map((k) => ({
                    value: k,
                    label: k,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Effective date"
                name="effectiveDate"
                rules={[{ required: true }]}
                style={{ flex: 1 }}
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Audience" name="targetType" rules={[{ required: true }]}>
              <Select
                options={(Object.keys(TARGET_LABEL) as NoticeTargetType[]).map((k) => ({
                  value: k,
                  label: TARGET_LABEL[k],
                }))}
              />
            </Form.Item>
            {watchedTarget === 'CLASS_SPECIFIC' && (
              <Form.Item label="Class" name="targetClassId" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
                />
              </Form.Item>
            )}
          </Form>
        )}
      </Drawer>

      {viewing && (
        <Drawer
          open
          title={viewing.title}
          size={520}
          onClose={() => setViewing(null)}
          extra={
            canEdit ? (
              <Button
                onClick={() => {
                  openEdit(viewing);
                  setViewing(null);
                }}
              >
                Edit
              </Button>
            ) : undefined
          }
        >
          <Space orientation="vertical" style={{ width: '100%' }} size={12}>
            <Space>
              <Tag color={CATEGORY_COLOR[viewing.category]}>{viewing.category}</Tag>
              <Tag>{TARGET_LABEL[viewing.targetType]}{viewing.targetClassName ? ` · ${viewing.targetClassName}` : ''}</Tag>
              <Tag color={viewing.active ? 'success' : 'default'}>{viewing.active ? 'Active' : 'Inactive'}</Tag>
            </Space>
            <Typography.Text type="secondary">
              Effective {dayjs(viewing.effectiveDate).format('D MMM YYYY')}
            </Typography.Text>
            <Card>
              <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                {viewing.body}
              </Typography.Paragraph>
            </Card>
          </Space>
        </Drawer>
      )}
    </div>
  );
}
